import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { companyNameValidation, nameValidation, requiredValidation } from 'validators';
import { AnimatedTextField, Button, MenuItem, Select, TextField, Typography } from 'seagull';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { StepTemplate } from '../StepTemplate';
import { useQboInVob } from '../useQboInVob';
import { Step } from '../VendorOnboardingModal.utils';
import styles from './FirstStep.module.scss';
const Section = (props)=>/*#__PURE__*/ _jsxs("div", {
        className: styles.section,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                children: props.title
            }),
            props.children
        ]
    });
export function FirstStep(props) {
    const { initialState, onSubmit } = props;
    const { t: firstStepTexts } = useLocaleKeys('pages.vendorOnboarding.firstStep');
    const { control, handleSubmit, trigger, getFieldState } = useForm({
        mode: 'onChange',
        defaultValues: initialState
    });
    const [showNameSection, setShowNameSection] = useState(true);
    const { showQboStep } = useQboInVob();
    const numOfInvoicesOptions = useMemo(()=>[
            {
                label: firstStepTexts.numInvoicesOptions.lessThan10(),
                value: '0'
            },
            {
                label: firstStepTexts.numInvoicesOptions.tenToFifty(),
                value: '10'
            },
            {
                label: firstStepTexts.numInvoicesOptions.fiftyToTwoFifty(),
                value: '50'
            },
            {
                label: firstStepTexts.numInvoicesOptions.twoFiftyToOneK(),
                value: '250'
            },
            {
                label: firstStepTexts.numInvoicesOptions.moreThanOneK(),
                value: '1000'
            }
        ], []);
    const businessDescriptionSuggestions = useMemo(()=>[
            firstStepTexts.suggestions.bookkeepingFirm(),
            firstStepTexts.suggestions.taxAdvisory(),
            firstStepTexts.suggestions.marketingAgency(),
            firstStepTexts.suggestions.consultingServices(),
            firstStepTexts.suggestions.accountingPractice()
        ], []);
    const firstNameValidator = useMemo(()=>nameValidation(undefined, {
            required: firstStepTexts.validation.firstNameRequired()
        }), []);
    const lastNameValidator = useMemo(()=>nameValidation(undefined, {
            required: firstStepTexts.validation.lastNameRequired()
        }), []);
    const companyNameValidator = useMemo(()=>companyNameValidation(undefined, {
            required: firstStepTexts.validation.companyNameRequired()
        }), []);
    const numOfInvoicesValidator = useMemo(()=>requiredValidation(firstStepTexts.validation.numInvoicesRequired()), []);
    useOnMount(()=>{
        trigger([
            'name'
        ]).then(()=>{
            setShowNameSection(getFieldState('name').invalid);
        });
    });
    return /*#__PURE__*/ _jsx("form", {
        onSubmit: handleSubmit(onSubmit),
        children: /*#__PURE__*/ _jsx(StepTemplate, {
            currentStep: Step.First,
            stepper: firstStepTexts.stepper({
                current: '1',
                total: showQboStep ? '3' : '2'
            }),
            header: firstStepTexts.header(),
            subHeader: firstStepTexts.subHeader(),
            footer: /*#__PURE__*/ _jsx(Button, {
                testId: "vob-step-".concat(Step.First, "-submit"),
                type: "submit",
                className: styles.submitBtn,
                bi: {
                    name: 'next'
                },
                children: firstStepTexts.next()
            }),
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.root,
                children: [
                    showNameSection && /*#__PURE__*/ _jsxs("div", {
                        className: styles.nameSection,
                        children: [
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "name.first",
                                rules: firstNameValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        collapseHelperText: false
                                    }, connectControllerToTextField(renderProps)), {
                                        testId: "vob-first-name",
                                        placeholder: firstStepTexts.firstNamePlaceholder(),
                                        bi: {
                                            name: 'first_name'
                                        }
                                    }))
                            }),
                            /*#__PURE__*/ _jsx(Controller, {
                                control: control,
                                name: "name.last",
                                rules: lastNameValidator,
                                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                        collapseHelperText: false
                                    }, connectControllerToTextField(renderProps)), {
                                        testId: "vob-last-name",
                                        placeholder: firstStepTexts.lastNamePlaceholder(),
                                        bi: {
                                            name: 'last_name'
                                        }
                                    }))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Controller, {
                        control: control,
                        name: "companyName",
                        rules: companyNameValidator,
                        render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                collapseHelperText: false
                            }, connectControllerToTextField(renderProps)), {
                                testId: "vob-company-name",
                                placeholder: firstStepTexts.companyNamePlaceholder(),
                                bi: {
                                    name: 'company_name'
                                }
                            }))
                    }),
                    /*#__PURE__*/ _jsx(Section, {
                        title: firstStepTexts.businessDescriptionSectionTitle(),
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "businessDescription",
                            rules: {
                                required: firstStepTexts.validation.businessDescriptionRequired()
                            },
                            render: (renderProps)=>{
                                const connected = connectControllerToTextField(renderProps);
                                return /*#__PURE__*/ _jsx(AnimatedTextField, _object_spread_props(_object_spread({}, connected), {
                                    suggestions: businessDescriptionSuggestions,
                                    staticPrefix: firstStepTexts.businessDescriptionPrefix(),
                                    collapseHelperText: true,
                                    showHelperTextOnFocusOnly: true,
                                    helperText: connected.helperText || firstStepTexts.businessDescriptionHelperText(),
                                    testId: "vob-business-description",
                                    bi: {
                                        name: 'business_description'
                                    }
                                }));
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(Section, {
                        title: firstStepTexts.numInvoicesSectionTitle(),
                        children: /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "numOfInvoices",
                            rules: numOfInvoicesValidator,
                            render: (renderProps)=>/*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                                    collapseHelperText: false
                                }, connectControllerToTextField(renderProps)), {
                                    testId: "vob-num-of-invoices",
                                    placeholder: firstStepTexts.numInvoicesPlaceholder(),
                                    bi: {
                                        name: 'total_monthly_invoices'
                                    },
                                    children: numOfInvoicesOptions.map((param)=>{
                                        let { label, value } = param;
                                        return /*#__PURE__*/ _jsx(MenuItem, {
                                            value: value,
                                            testId: "noi-option-".concat(value),
                                            children: label
                                        }, value);
                                    })
                                }))
                        })
                    })
                ]
            })
        })
    });
}
